import React from 'react';
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';

import { useTranslation } from 'react-i18next';

interface MermaidToolbarProps {
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>;
  handleDownloadPNG: () => void;
  handleReset?: () => void;
  isMaximized: boolean;
  onToggleMaximize: () => void;
}

// Shared styles for all buttons (layout only; colors handled by Tailwind classes)
const buttonBaseStyle: React.CSSProperties = {
  borderRadius: '4px',
  padding: '6px 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  outline: 'none',
  backdropFilter: 'blur(4px)',
};

// Tailwind classes for button theming (base + hover)
const buttonClassName =
  'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 shadow-sm hover:bg-white/95 hover:dark:bg-gray-700/95 hover:text-gray-800 hover:dark:text-gray-200 hover:shadow-md';

// Download button at top right
const downloadButtonStyle: React.CSSProperties = {
  ...buttonBaseStyle,
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: 2,
};

// Zoom controls container at bottom right
const zoomControlsStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 12,
  right: 12,
  display: 'flex',
  gap: '6px',
  zIndex: 2,
};

const iconProps = {
  width: 16,
  height: 16,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export const MermaidToolbar: React.FC<MermaidToolbarProps> = ({
  transformRef,
  handleDownloadPNG,
  handleReset,
  isMaximized,
  onToggleMaximize,
}) => {
  const { t } = useTranslation('pleri/chat');
  // Download button (top right)
  const renderDownloadButton = () => (
    <button
      aria-label={t('downloadPng')}
      onClick={handleDownloadPNG}
      style={downloadButtonStyle}
      className={buttonClassName}
    >
      <svg {...iconProps} viewBox="0 0 24 24">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </button>
  );

  // Zoom controls (bottom right)
  const zoomButtons = [
    {
      id: 'zoomOut',
      aria: 'Zoom out',
      onClick: () => transformRef.current?.zoomOut(),
      icon: (
        <svg {...iconProps} viewBox="0 0 24 24">
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      ),
    },
    {
      id: 'reset',
      aria: 'Reset view',
      onClick: () =>
        handleReset ? handleReset() : transformRef.current?.resetTransform(),
      icon: (
        <svg {...iconProps} viewBox="0 0 24 24">
          <polyline points="1 4 1 10 7 10" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
        </svg>
      ),
    },
    {
      id: 'zoomIn',
      aria: 'Zoom in',
      onClick: () => transformRef.current?.zoomIn(),
      icon: (
        <svg {...iconProps} viewBox="0 0 24 24">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      ),
    },
    {
      id: 'toggleMaximize',
      aria: isMaximized ? 'Minimize diagram' : 'Maximize diagram',
      onClick: onToggleMaximize,
      icon: isMaximized ? (
        <svg {...iconProps} viewBox="0 0 24 24">
          <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
        </svg>
      ) : (
        <svg {...iconProps} viewBox="0 0 24 24">
          <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
        </svg>
      ),
    },
  ];

  const renderZoomControls = () => (
    <div
      style={zoomControlsStyle}
      role="toolbar"
      aria-label={t('diagramZoomControls')}
    >
      {zoomButtons.map((btn) => (
        <button
          key={btn.id}
          onClick={btn.onClick}
          aria-label={btn.aria}
          tabIndex={0}
          style={buttonBaseStyle}
          className={buttonClassName}
        >
          {btn.icon}
        </button>
      ))}
    </div>
  );

  return (
    <>
      {renderDownloadButton()}
      {renderZoomControls()}
    </>
  );
};
